import '../polyfills';

import 'animate.css';
import 'antd/dist/reset.css';
import 'bootstrap/scss/bootstrap.scss';
import 'react-loading-skeleton/dist/skeleton.css';

import './styles/styles.scss';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Breadcrumbs, GlobalHandlers, init, Replay } from '@sentry/react';
import { ConfigProvider } from 'antd';
import FontFaceObserver from 'fontfaceobserver';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { DEFAULT_FONT, DEFAULT_FONT_BOLD } from '@/shared/ui';
import { ACCENT_GREEN_500, ENV, ENV_ENUM } from '@/shared/config';
import App from './App';

init({
  dsn:
    ENV === ENV_ENUM.customer
      ? 'https://e14ad8c8ec14220f55a0ec1cfd68c823@o4506115484024832.ingest.sentry.io/4506115490643968'
      : '',
  integrations: [
    new GlobalHandlers({
      onerror: true,
      onunhandledrejection: false,
    }),
    new Breadcrumbs({
      console: true,
      fetch: true,
      xhr: true,
    }),
    new Replay({
      maskAllText: false,
    }),
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  ignoreErrors: ['CanceledError'],
});

const fonts = [DEFAULT_FONT, DEFAULT_FONT_BOLD];

// IndexedDB is opened lazily by the report page only after the profile is validated by a
// successful report fetch, so an invalid/stale profile id in the URL never creates junk databases.
const profileIdKey = window.location.pathname.split('/')[3];
if (profileIdKey) {
  localStorage.removeItem(`defaultValueHeatmap-${profileIdKey}`);
}

Promise.all(fonts.map((font) => new FontFaceObserver(font).load()))
  .then(() => {
    console.log('Fonts has loaded.');
  })
  .catch(() => {
    console.log('Fonts failed to load.');
  });

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container);
root.render(
  <CacheProvider
    value={createCache({
      key: 'css',
      nonce: window.__CSP_NONCE__,
    })}
  >
    <ConfigProvider
      csp={{ nonce: window.__CSP_NONCE__ }}
      theme={{
        token: {
          fontFamily: 'Lato, Arial, Helvetica, sans-serif',
        },
        components: {
          Switch: {
            colorPrimary: ACCENT_GREEN_500,
            colorPrimaryHover: ACCENT_GREEN_500,
          },
        },
      }}
    >
      <App />
    </ConfigProvider>
  </CacheProvider>,
);
